'use strict';

var uint8arrayExtras = require('uint8array-extras');
var base64 = require('./util/base64.js');
var index = require('../xdr/index.js');
var hashing = require('./hashing.js');
var strkey = require('./strkey.js');
var operation = require('./operation.js');
var memo = require('./memo.js');
var transaction_base = require('./transaction_base.js');
var decode_encode_muxed_account = require('./util/decode_encode_muxed_account.js');
var transactionEnvelope = require('../xdr/generated/transaction-envelope.js');
var transactionSignaturePayloadTaggedTransaction = require('../xdr/generated/transaction-signature-payload-tagged-transaction.js');
var transactionSignaturePayload = require('../xdr/generated/transaction-signature-payload.js');
var transactionV1Envelope = require('../xdr/generated/transaction-v1-envelope.js');
var transactionV0Envelope = require('../xdr/generated/transaction-v0-envelope.js');
var hashIdPreimageOperationId = require('../xdr/generated/hash-id-preimage-operation-id.js');
var transaction = require('../xdr/generated/transaction.js');
var hash = require('../xdr/generated/hash.js');
var transactionV0 = require('../xdr/generated/transaction-v0.js');
var hashIdPreimage = require('../xdr/generated/hash-id-preimage.js');
var publicKey = require('../xdr/generated/public-key.js');
var claimableBalanceId = require('../xdr/generated/claimable-balance-id.js');

class Transaction extends transaction_base.TransactionBase {
  _envelopeType;
  _source = "";
  _memo;
  _sequence;
  _operations;
  _timeBounds;
  _ledgerBounds;
  _minAccountSequence;
  _minAccountSequenceAge;
  _minAccountSequenceLedgerGap;
  _extraSigners;
  /**
   * @param envelope - transaction envelope object or base64 encoded string
   * @param networkPassphrase - passphrase of the target stellar network
   *     (e.g. "Public Global Stellar Network ; September 2015")
   */
  constructor(envelope, networkPassphrase) {
    if (typeof envelope === "string") {
      const bytes = base64.base64ToUint8Array(envelope);
      envelope = transactionEnvelope.TransactionEnvelope.fromXdr(bytes);
    }
    const envelopeType = envelope.type;
    if (envelopeType !== "envelopeTypeTxV0" && envelopeType !== "envelopeTypeTx") {
      throw new Error(
        `Invalid TransactionEnvelope: expected an envelopeTypeTxV0 or envelopeTypeTx but received an ${envelopeType}.`
      );
    }
    const txEnvelope = envelope.value;
    const tx = txEnvelope.tx;
    const fee = tx.fee.toString();
    const signatures = (txEnvelope.signatures || []).slice();
    super(tx, signatures, fee, networkPassphrase);
    this._envelopeType = envelopeType;
    this._memo = tx.memo;
    this._sequence = tx.seqNum.toString();
    switch (this._envelopeType) {
      case "envelopeTypeTxV0":
        this._source = strkey.StrKey.encodeEd25519PublicKey(
          tx.sourceAccountEd25519.toBytes()
        );
        break;
      default:
        this._source = decode_encode_muxed_account.encodeMuxedAccountToAddress(
          tx.sourceAccount
        );
        break;
    }
    let condV2 = null;
    let timeBounds = null;
    switch (this._envelopeType) {
      case "envelopeTypeTxV0":
        timeBounds = tx.timeBounds;
        break;
      case "envelopeTypeTx": {
        const cond = tx.cond;
        switch (cond.type) {
          case "precondTime":
            timeBounds = cond.value;
            break;
          case "precondV2":
            condV2 = cond.value;
            timeBounds = condV2.timeBounds;
            break;
        }
        break;
      }
    }
    if (timeBounds) {
      this._timeBounds = {
        minTime: timeBounds.minTime.toString(),
        maxTime: timeBounds.maxTime.toString()
      };
    }
    if (condV2) {
      const ledgerBounds = condV2.ledgerBounds;
      if (ledgerBounds) {
        this._ledgerBounds = {
          minLedger: ledgerBounds.minLedger,
          maxLedger: ledgerBounds.maxLedger
        };
      }
      const minSeq = condV2.minSeqNum;
      if (minSeq) {
        this._minAccountSequence = minSeq.toString();
      }
      this._minAccountSequenceAge = condV2.minSeqAge;
      this._minAccountSequenceLedgerGap = condV2.minSeqLedgerGap;
      this._extraSigners = condV2.extraSigners;
    }
    const operations = tx.operations || [];
    this._operations = operations.map(
      (op) => operation.Operation.fromXdrObject(op)
    );
  }
  /**
   * The time bounds for this transaction, with `minTime` and `maxTime` as
   * 64-bit unix timestamps (strings).
   */
  get timeBounds() {
    return this._timeBounds;
  }
  set timeBounds(_value) {
    throw new Error("Transaction is immutable");
  }
  /**
   * The ledger bounds for this transaction, with `minLedger` (uint32) and
   * `maxLedger` (uint32, or 0 for no upper bound).
   */
  get ledgerBounds() {
    return this._ledgerBounds;
  }
  set ledgerBounds(_value) {
    throw new Error("Transaction is immutable");
  }
  /** The minimum account sequence (64-bit, as a string). */
  get minAccountSequence() {
    return this._minAccountSequence;
  }
  set minAccountSequence(_value) {
    throw new Error("Transaction is immutable");
  }
  /** The minimum account sequence age (64-bit number of seconds). */
  get minAccountSequenceAge() {
    return this._minAccountSequenceAge;
  }
  set minAccountSequenceAge(_value) {
    throw new Error("Transaction is immutable");
  }
  /** The minimum account sequence ledger gap (32-bit number of ledgers). */
  get minAccountSequenceLedgerGap() {
    return this._minAccountSequenceLedgerGap;
  }
  set minAccountSequenceLedgerGap(_value) {
    throw new Error("Transaction is immutable");
  }
  /**
   * Array of extra signers as XDR objects; use {@link SignerKey.encodeSignerKey}
   * to convert to StrKey strings.
   */
  get extraSigners() {
    return this._extraSigners;
  }
  set extraSigners(_value) {
    throw new Error("Transaction is immutable");
  }
  /** The sequence number for this transaction. */
  get sequence() {
    return this._sequence;
  }
  set sequence(_value) {
    throw new Error("Transaction is immutable");
  }
  /** The source account for this transaction. */
  get source() {
    return this._source;
  }
  set source(_value) {
    throw new Error("Transaction is immutable");
  }
  /** The list of operations in this transaction. */
  get operations() {
    return this._operations;
  }
  set operations(_value) {
    throw new Error("Transaction is immutable");
  }
  /** The memo attached to this transaction. */
  get memo() {
    return memo.Memo.fromXdrObject(this._memo);
  }
  set memo(_value) {
    throw new Error("Transaction is immutable");
  }
  /**
   * Returns the "signature base" of this transaction, which is the value
   * that, when hashed, should be signed to create a signature that
   * validators on the Stellar Network will accept.
   *
   * It is composed of a 4 prefix bytes followed by the xdr-encoded form
   * of this transaction.
   */
  signatureBase() {
    let tx = this.tx;
    if (this._envelopeType === "envelopeTypeTxV0") {
      tx = transaction.Transaction.fromXdr(
        uint8arrayExtras.concatUint8Arrays([
          // TransactionV0 is a transaction with the AccountID discriminant
          // stripped off, we need to put it back to build a valid transaction
          // which we can use to build a TransactionSignaturePayloadTaggedTransaction
          new Uint8Array(4),
          // AccountID discriminant: publicKeyTypeEd25519 = 0
          tx.toXdr()
        ])
      );
    }
    const taggedTransaction = transactionSignaturePayloadTaggedTransaction.TransactionSignaturePayloadTaggedTransaction.envelopeTypeTx(
      tx
    );
    const txSignature = new transactionSignaturePayload.TransactionSignaturePayload({
      networkId: hash.Hash.fromXdr(hashing.hash(this.networkPassphrase)),
      taggedTransaction
    });
    return txSignature.toXdr();
  }
  /**
   * To envelope returns a xdr.TransactionEnvelope which can be submitted to the network.
   */
  toEnvelope() {
    const rawTx = this.tx.toXdr();
    const signatures = this.signatures.slice();
    let envelope;
    switch (this._envelopeType) {
      case "envelopeTypeTxV0":
        envelope = transactionEnvelope.TransactionEnvelope.envelopeTypeTxV0(
          new transactionV0Envelope.TransactionV0Envelope({
            tx: transactionV0.TransactionV0.fromXdr(rawTx),
            // make a copy of tx
            signatures
          })
        );
        break;
      case "envelopeTypeTx":
        envelope = transactionEnvelope.TransactionEnvelope.envelopeTypeTx(
          new transactionV1Envelope.TransactionV1Envelope({
            tx: transaction.Transaction.fromXdr(rawTx),
            // make a copy of tx
            signatures
          })
        );
        break;
      default:
        throw new Error(
          `Invalid TransactionEnvelope: expected an envelopeTypeTxV0 or envelopeTypeTx but received an ${this._envelopeType}.`
        );
    }
    return envelope;
  }
  /**
   * Calculate the claimable balance ID for an operation within the transaction.
   *
   * @param opIndex - the index of the CreateClaimableBalance op
   *
   * @throws for invalid `opIndex` value, if op at `opIndex` is not
   *    `CreateClaimableBalance`, or for general XDR un/marshalling failures
   *
   * @see https://github.com/stellar/go/blob/d712346e61e288d450b0c08038c158f8848cc3e4/txnbuild/transaction.go#L392-L435
   *
   */
  getClaimableBalanceId(opIndex) {
    if (!Number.isInteger(opIndex) || opIndex < 0 || opIndex >= this.operations.length) {
      throw new RangeError("invalid operation index");
    }
    const op = this.operations[opIndex];
    if (op === void 0) {
      throw new RangeError("invalid operation index");
    }
    try {
      operation.Operation.createClaimableBalance(
        op
      );
    } catch (err) {
      throw new TypeError(
        `expected createClaimableBalance, got ${op.type}: ${String(err)}`
      );
    }
    const account = strkey.StrKey.decodeEd25519PublicKey(
      decode_encode_muxed_account.extractBaseAddress(this.source)
    );
    const operationId = hashIdPreimage.HashIdPreimage.envelopeTypeOpId(
      new hashIdPreimageOperationId.HashIdPreimageOperationId({
        sourceAccount: publicKey.PublicKey.publicKeyTypeEd25519(account),
        seqNum: index.Int64.fromString(this.sequence),
        opNum: opIndex
      })
    );
    const opIdHash = hashing.hash(operationId.toXdr("raw"));
    const balanceId = claimableBalanceId.ClaimableBalanceId.claimableBalanceIdTypeV0(
      new hash.Hash(opIdHash)
    );
    return balanceId.toXdr("hex");
  }
}

exports.Transaction = Transaction;
//# sourceMappingURL=transaction.js.map
